import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, RefreshCw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SRC = { manual: "bg-amber-100 text-amber-800", supplier: "bg-emerald-100 text-emerald-800", base: "bg-slate-100 text-slate-600" };

export function ProductCostPanel({ productId, canManage }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [net, setNet] = useState("");
  const [reason, setReason] = useState("");
  const [lock, setLock] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await api.get(`/products/${productId}/offers`)).data); }
    catch (e) { if (e?.response?.status === 403) setData({ forbidden: true }); }
  }, [productId]);
  useEffect(() => { if (productId) load(); }, [productId, load]);

  if (!data || data.forbidden) return null;
  const cost = data.effective_cost || {};

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/products/${productId}/cost-override`, { net_cost: net || "0", reason, locked: lock });
      toast.success(t("cost.override_saved")); setNet(""); setReason(""); await load();
    } catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try { await api.delete(`/products/${productId}/cost-override`); toast.success(t("cost.override_cleared")); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, t)); } finally { setBusy(false); }
  };

  return (
    <Card className="p-6" data-testid="cost-panel">
      <h3 className="mb-4 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("cost.title")}</h3>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-2xl font-semibold tabular-nums" data-testid="cost-value">{cost.net_cost} €</span>
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${SRC[cost.source] || SRC.base}`} data-testid="cost-source">
          {t(`cost.source_${cost.source}`)}{cost.source_detail ? ` · ${cost.source_detail}` : ""}
        </span>
        {cost.locked && <Lock className="h-4 w-4 text-amber-600" title={t("cost.locked")} />}
        {cost.freshness && (() => {
          const days = Math.floor((Date.now() - new Date(cost.freshness)) / 86400000);
          const stale = cost.source === "supplier" && days > 90;
          return (
            <span className={`text-xs ${stale ? "font-medium text-amber-600" : "text-slate-400"}`} data-testid="cost-freshness">
              {stale ? t("cost.stale") : t("cost.synced")}: {new Date(cost.freshness).toLocaleDateString()}
            </span>
          );
        })()}
      </div>

      {data.offers?.length > 0 && (
        <div className="mt-4 space-y-1">
          <p className="text-xs font-medium text-slate-500">{t("cost.offers")}</p>
          {data.offers.map((o) => (
            <div key={o.id} className="flex items-center justify-between rounded border border-slate-100 px-3 py-1.5 text-sm dark:border-slate-800" data-testid="cost-offer-row">
              <span className="truncate">{o.supplier_name || "—"} <span className="text-slate-400">· {o.external_article_id}</span></span>
              <span className="tabular-nums">{o.net_cost} {o.currency} {o.pack_size && Number(o.pack_size) !== 1 ? `/ ${o.pack_size}` : ""}</span>
            </div>
          ))}
        </div>
      )}

      {canManage && (
        <div className="mt-5 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 dark:border-slate-800 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-xs">{t("cost.override_net")}</Label>
            <Input value={net} onChange={(e) => setNet(e.target.value)} placeholder="0.00" data-testid="cost-override-net" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs">{t("cost.override_reason")}</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cost-override-reason" />
          </div>
          <div className="flex items-end gap-2">
            <Button type="button" variant={lock ? "default" : "outline"} size="sm" onClick={() => setLock(!lock)} data-testid="cost-override-lock">
              {lock ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}{lock ? t("cost.lock_on") : t("cost.lock_off")}
            </Button>
          </div>
          <div className="flex gap-2 sm:col-span-4">
            <Button type="button" size="sm" disabled={busy || !net} onClick={save} data-testid="cost-override-save">{t("cost.set_override")}</Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={clear} data-testid="cost-override-clear">
              <RefreshCw className="h-4 w-4" />{t("cost.resume_feed")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
